import React, { useEffect, useState } from 'react';
import { myFilesFolderView, copyEnvelopeToMyFiles } from '../../../../../services/CommonService';
import { toast } from 'react-toastify';
import image from '../../../../../assets/svg/image.svg';
import docx from '../../../../../assets/svg/docx.svg';
import folder from '../../../../../assets/svg/folder.svg';
import pdf from '../../../../../assets/svg/pdf.svg';

const getFileIconAndClass = (fileName) => {
  const extension = fileName.split('.').pop().toLowerCase();
  switch (extension) {
    case 'pdf':
      return {
        icon: (
          <div className="icon-background">
            <img src={pdf} alt="PDF icon" style={{ height: 40, width: 40 }} />
          </div>
        ),
        class: 'file-card-pdf pdf-border',
        type: 'PDF',
      };
    case 'jpg':
    case 'jpeg':
    case 'png':
      return {
        icon: (
          <div className="icon-background">
            <img src={image} alt="Image icon" style={{ height: 40, width: 40 }} />
          </div>
        ),
        class: 'file-card-image image-border',
        type: 'IMAGE',
      };
    case 'gif':
      return {
        icon: (
          <div className="icon-background">
            <svg className="file-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 2H6C5.44772 2 5 2.44772 5 3V21C5 21.5523 5.44772 22 6 22H18C18.5523 22 19 21.5523 19 21V8L14 2Z" fill="#4dff88" />
              <path d="M14 2V8H19" fill="#66ff99" />
              <path d="M9 12L11 14L14 11L16 14H8L9 12Z" fill="#fff" />
            </svg>
          </div>
        ),
        class: 'file-card-image image-border',
        type: 'IMAGE',
      };
    case 'doc':
    case 'docx':
      return {
        icon: (
          <div className="icon-background">
            <img src={docx} alt="DOC icon" style={{ height: 40, width: 40 }} />
          </div>
        ),
        class: 'file-card-doc doc-border',
        type: 'DOC',
      };
    default:
      return {
        icon: (
          <div className="icon-background">
            <img src={folder} alt="Default icon" style={{ height: 40, width: 40 }} />
          </div>
        ),
        class: 'file-card-doc default-border',
        type: 'FILE',
      };
  }
};

const PasteToMyFilesModal = ({ show, handleClose, itemsToPaste = [], userId, onPasteSuccess }) => {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pasteLoading, setPasteLoading] = useState(false);

  useEffect(() => {
    if (show && userId) {
      fetchFolders(null);
    }
    if (show) {
      setCurrentFolder(null);
      setFolderStack([]);
      console.log('Items to paste in modal:', itemsToPaste);
    }
  }, [show, userId, itemsToPaste]);

  const fetchFolders = async (folderId) => {
    setLoading(true);
    try {
      const payload = { user_id: userId, folder_id: folderId };
      const response = await myFilesFolderView(payload);
      if (response.data.message === 'success') {
        const folders = response.data.data?.folders || [];
        const files = response.data.data?.files || [];
        console.log('Fetched folders:', folders);
        setFolders(folders);
        setFiles(files);
      } else {
        setFolders([]);
        setFiles([]);
        toast.error(response.data.message || 'Failed to fetch folders');
      }
    } catch (err) {
      setFolders([]);
      setFiles([]);
      toast.error('Failed to fetch folders');
    } finally {
      setLoading(false);
    }
  };

  const handleFolderClick = (folder) => {
    setFolderStack((prev) => (currentFolder ? [...prev, currentFolder] : prev));
    setCurrentFolder(folder);
    fetchFolders(folder.id);
  };

  const handleBack = () => {
    if (folderStack.length > 0) {
      const newStack = [...folderStack];
      const previousFolder = newStack.pop();
      setFolderStack(newStack);
      setCurrentFolder(previousFolder);
      fetchFolders(previousFolder ? previousFolder.id : null);
    } else {
      setCurrentFolder(null);
      setFolderStack([]);
      fetchFolders(null);
    }
  };

  const getBreadcrumbs = () => {
    let fullStack = [];
    fullStack.push({ id: null, folder_name: 'Home' });
    fullStack = fullStack.concat(folderStack);
    if (currentFolder && (!folderStack.length || folderStack[folderStack.length - 1]?.id !== currentFolder.id)) {
      fullStack.push(currentFolder);
    }

    return (
      <div className="d-flex align-items-center flex-wrap">
        {fullStack.map((folder, idx) => {
          const isLast = idx === fullStack.length - 1;
          return (
            <React.Fragment key={folder?.id ?? 'root'}>
              {idx !== 0 && (
                <span className="breadcrumbSeparator" style={{ margin: '0 8px' }}>
                  <i className="bi bi-chevron-right textMuted" />
                </span>
              )}
              {isLast ? (
                <span
                  className="breadcrumbItem activeCrumb"
                  style={{ color: '#222', fontWeight: 700 }}
                  title={folder.folder_name}
                >
                  {folder.folder_name}
                </span>
              ) : (
                <span
                  className="breadcrumbItem"
                  onClick={() => {
                    if (folder.id === null) {
                      setCurrentFolder(null);
                      setFolderStack([]);
                      fetchFolders(null);
                    } else {
                      setCurrentFolder(folder);
                      setFolderStack(fullStack.slice(1, idx));
                      fetchFolders(folder.id);
                    }
                  }}
                  style={{ cursor: 'pointer', color: '#3b82f6', fontWeight: 500 }}
                  title={folder.folder_name}
                >
                  {folder.folder_name}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  const handlePasteHere = async () => {
    if (!itemsToPaste || itemsToPaste.length === 0) {
      toast.error('No items selected to paste');
      return;
    }
    setPasteLoading(true);
    try {
      const payload = {
        ...itemsToPaste[0],
        target_folder_id: currentFolder ? currentFolder.id : null,
        user_id: userId
      };
      console.log('API Payload:', payload);
      await copyEnvelopeToMyFiles(payload);
      toast.success('Item pasted successfully!');
      if (onPasteSuccess) onPasteSuccess();
      handleClose();
    } catch (err) {
      console.error('Paste error:', err);
      toast.error(err.message || 'Failed to paste item');
    } finally {
      setPasteLoading(false);
    }
  };

  return (
    show && (
      <>
        {pasteLoading && (
          <div className="page-loading" style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src="/images/loader.gif" alt="loader" />
          </div>
        )}
        <div className="modalOverlay">
          <div className="siteFilesModal">
            <div className="modalHeader">
              <h5>Paste to My Files</h5>
              <button
                onClick={handleClose}
                className="btnClose"
                aria-label="Close modal"
              >
                <span>×</span>
              </button>
            </div>
            <div className="modalBody">
              <div className="folderHeader rounded">
                <div className="headerContainer">
                  {getBreadcrumbs()}
                </div>
              </div>
              <div className="fileListItem">
                {loading ? (
                  <div className="text-center p-4">Loading...</div>
                ) : (folders.length === 0 && files.length === 0) ? (
                  <div className="text-muted">No files or folders found.</div>
                ) : (
                  [...folders.map(f => ({ ...f, _type: 'folder' })), ...files.map(f => ({ ...f, _type: 'file' }))]
                    .map(item => {
                      if (item._type === 'folder') {
                        return (
                          <div
                            key={`folder-${item.id}`}
                            className="file-card file-card-folder folder-border cursor-pointer"
                            onClick={() => handleFolderClick(item)}
                          >
                            <div className="file-icon-container">
                              <div className="icon-background">
                                <svg
                                  className="plain file-icon"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{ width: 40, height: 40 }}
                                >
                                  <path
                                    d="M4 6H9L11 8H20C20.5523 8 21 8.44772 21 9V19C21 19.5523 20.5523 20 20 20H4C3.44772 20 3 19.5523 3 19V7C3 6.44772 3.44772 6 4 6Z"
                                    fill="#ffcc00"
                                  />
                                  <path
                                    d="M9 6L11 8H20V9H9L7 7H4V6H9Z"
                                    fill="#ffd633"
                                  />
                                </svg>
                              </div>
                            </div>
                            <div className="file-name">{item.folder_name}</div>
                            <div className="file-type">FOLDER</div>
                            <div className="file-count position-absolute top-0 end-0 bg-light rounded-circle d-flex justify-content-center align-items-center px-2 py-0">
                              {item.file_count}
                            </div>
                          </div>
                        );
                      } else {
                        const { icon, class: cardClass, type } = getFileIconAndClass(item.file_name);
                        return (
                          <div
                            key={`file-${item.id}`}
                            className={`file-card file-card-folder folder-border cursor-pointer file-card ${cardClass}`}
                          >
                            <div className="file-icon-container">{icon}</div>
                            <div className="file-name">{item.file_name}</div>
                            <div className="file-type">{type}</div>
                          </div>
                        );
                      }
                    })
                )}
              </div>
            </div>
            <div className="modalFooter">
              <button
                onClick={handleClose}
                className="btn btnOutlineSecondary"
                style={{ marginRight: 12 }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasteHere}
                className="btn btnGradient"
                disabled={pasteLoading || !itemsToPaste.length}
                style={{ minWidth: 120 }}
              >
                {pasteLoading ? "Pasting..." : "Paste Here"}
              </button>
            </div>
          </div>
        </div>
      </>
    )
  );
};

export default PasteToMyFilesModal;